import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createEnquiry,
  updateEnquiry,
  ApprovePartyList,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
  getUnits,
  getDivisionUnits,
  GetNextEnquiryNo,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DraftsIcon from "@mui/icons-material/Drafts";    
import SaveAltIcon from "@mui/icons-material/SaveAlt";    
import EditNoteIcon from "@mui/icons-material/EditNote";
import { debounce } from "lodash";

export default function EnquiryForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  // API for state/city
  const API_BASE_URL = "https://api.countrystatecity.in/v1";
  const API_KEY = "dc87a0d6844b70cf9f1294bdfe6d00cdfa09ebc7adf7b26d3420a0fe1bd10491";

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const [enquiryData] = useState(() => location.state?.data ?? null);
  const isEditMode = !!enquiryData;

  // Party & Contact Persons
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partySearch, setPartySearch] = useState("");
  const [contactPersons, setContactPersons] = useState([]);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);

  // Master data for table
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [itemsMap, setItemsMap] = useState({});
  const [units, setUnits] = useState([]);
  const [divisionList, setDivision] = useState([]);

  const [formData, setFormData] = useState(() => ({
    enquiry_no: enquiryData?.enquiry_no || "",
    company_name: enquiryData?.company_name || "",
    customer_name: enquiryData?.customer_name || enquiryData?.contact_person || "",
    email: enquiryData?.email || "",
    phone: enquiryData?.phone || "",
    address: enquiryData?.address || "",
    city: enquiryData?.city || "",
    state: enquiryData?.state || "",
    country: enquiryData?.country || "India",
    enquiry_source: enquiryData?.enquiry_source || "",
    enq_type: enquiryData?.enq_type || "",
    follow_up_date: formatDateForInput(enquiryData?.follow_up_date),
    status: enquiryData?.status || "New",
    division: storedDivision?.division_id || enquiryData?.division || "",
  }));

  const initialLineItem = {
    category: null,
    sub_category: null,
    item: null,
    description: "",
    quantity: "",
    unit: "",
    rate: "",
    amount: 0,
  };

  const initialNote = { note_text: "" };

  const [lineItems, setLineItems] = useState(() => {
    if (enquiryData?.line_items?.length > 0) {
      return enquiryData.line_items.map((it) => ({
        category: it.category ? { id: it.category, name: it.category_name || it.category } : it.category_name ? { id: it.category, name: it.category_name } : null,
        sub_category: it.sub_category ? { subcat_id: it.sub_category, name: it.sub_category_name || "" } : null,
        item: it.item ? { id: it.item, name: it.item_name || it.item, rate: it.rate || 0 } : it.item_name ? { id: it.item, name: it.item_name, rate: it.rate || 0 } : null,
        description: it.description || it.item_name || "",
        quantity: it.quantity?.toString() || "",
        unit: it.unit || "",
        rate: it.rate?.toString() || "",
        amount: it.amount || 0,
      }));
    }
    if (enquiryData?.items?.length > 0) {
      return enquiryData.items.map((it) => ({
        category: it.category ? { id: it.category, name: it.category_name } : null,
        sub_category: it.sub_category ? { subcat_id: it.sub_category, name: it.sub_category_name } : null,
        item: it.item ? { id: it.item, name: it.item_name, rate: it.rate || 0 } : null,
        description: it.description || "",
        quantity: it.quantity?.toString() || "",
        unit: it.unit || "",
        rate: it.rate?.toString() || "",
        amount: it.amount || 0,
      }));
    }
    return [{ ...initialLineItem }];
  });

  const [notes, setNotes] = useState(() => {
    if (enquiryData?.notes?.length > 0) {
      return enquiryData.notes.map((note) => ({
        note_text: note.note_text || "",
      }));
    }
    return [{ ...initialNote }];
  });

  const [errors, setErrors] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fetch masters on mount
  useEffect(() => {
    const init = async () => {
      try {
        const catRes = await getCategories();
        setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);

        try {
          const divData = await getDivisionUnits();
          setUnits(divData.units || []);
          setDivision(divData.divisions || []);
        } catch {
          const u = await getUnits();
          setUnits(u.results || u.units || u || []);
        }

        // next enquiry no
        if (!isEditMode) {
          try {
            const next = await GetNextEnquiryNo();
            const enquiryNo = next?.enquiry_no || next?.data?.enquiry_no || next?.results?.enquiry_no;
            if (enquiryNo) {
              setFormData((prev) => ({ ...prev, enquiry_no: enquiryNo }));
            }
          } catch (e) {
            console.error("GetNextEnquiryNo API Error:", e);
          }
        }

        // Preload Party list
        const partiesResponse = await ApprovePartyList(1, { name: "" });
        const partiesList = Array.isArray(partiesResponse) ? partiesResponse : partiesResponse?.results || [];
        setPartyOptions(partiesList);

        // Edit Mode: Contacts merge logic
        if (isEditMode && formData.company_name) {
          const selectedParty = partiesList.find(
            (p) => (p.company_name || p.name) === formData.company_name
          );
          if (selectedParty) {
            let unifiedContacts = [];
            if (selectedParty.name) {
              unifiedContacts.push({
                title: "",
                contact_name: selectedParty.name,
                contact_no: selectedParty.contact || "",
                designation: "Primary Contact",
              });
            }

            const additionalContacts = (selectedParty.contact_persons || []).filter(
              (ap) => ap.contact_name?.toLowerCase() !== selectedParty.name?.toLowerCase()
            );

            unifiedContacts = [...unifiedContacts, ...additionalContacts];
            setContactPersons(unifiedContacts);
            
            const matchedCP = unifiedContacts.find(
              (cp) => `${cp.title || ""} ${cp.contact_name || ""}`.trim() === formData.customer_name
            );
            if (matchedCP) setSelectedContactPerson(matchedCP);
          }
        }

        // preload subcats & items for edit mode
        if (lineItems.length > 0 && isEditMode) {
          lineItems.forEach(async (row, idx) => {
            if (row.category?.id) {
              const subcats = await fetchSubCategories(row.category.id);
              setSubcategories((prev) => {
                const copy = [...prev];
                copy[idx] = subcats.results || subcats;
                return copy;
              });
              const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, row.sub_category?.subcat_id || 0);
              const mapped = (itemData.results || itemData || []).map((it) => ({
                id: it.id,
                name: it.name,
                description: it.description,
                unit: it.unit,
                unit_name: it.unit_name,
                rate: it.rate || it.unit_price || it.selling_price || it.price || 0,
              }));
              setItemsMap((prev) => ({ ...prev, [idx]: mapped }));
            }
          });
        }
      } catch (err) {
        console.error("init error", err);
      }
    };
    init();
  }, [isEditMode]);

  const getParties = async (filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(1, filters);
      const results = Array.isArray(response) ? response : response?.results || [];
      setPartyOptions(results);
    } catch (err) {
      console.error("Error fetching parties:", err);
    } finally {
      setPartyLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getParties({ name: partySearch });
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [partySearch]);

  const fetchStatesForIndia = async () => {
    setLoadingStates(true);
    try {
      const response = await fetch(`${API_BASE_URL}/countries/IN/states`, {
        headers: { "X-CSCAPI-KEY": API_KEY },
      });
      const data = await response.json();
      if (Array.isArray(data)) setStates(data);
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCitiesForState = async (stateCode) => {
    setLoadingCities(true);
    try {
      const response = await fetch(`${API_BASE_URL}/countries/IN/states/${stateCode}/cities`, {
        headers: { "X-CSCAPI-KEY": API_KEY },
      });
      const data = await response.json();
      if (Array.isArray(data)) setCities(data);
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    fetchStatesForIndia();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d*$/.test(value)) return;
    if (name === "state" || name === "city") return;
    setFormData((prev) => ({ ...prev, [name]: value }));

    let errorMsg = "";
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) errorMsg = "Invalid email format";
    }
    if (name === "phone") {
      if (value.length > 0 && value.length < 10) errorMsg = "Phone must be 10 digits";
      if (value.length > 10) errorMsg = "Can't exceed 10 digits";
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  // === ITEM TABLE QUANTITY & RATE MANUAL INPUT CHANGES ===
  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index][field] = value;

      const qty = parseFloat(updated[index].quantity) || 0;
      const rate = parseFloat(updated[index].rate) || 0;
      updated[index].amount = qty * rate;

      return updated;
    });
  };

  // ══════════════════════════════════════════════════════════════════
  // 🟢 AUTO FILL RATE & UNIT (UOM) ON ITEM SELECTION
  // ══════════════════════════════════════════════════════════════════
  const handleItemAutocompleteChange = async (index, key, value) => {
    const updated = [...lineItems];
    let row = { ...updated[index] };

    switch (key) {
      case "category":
        row = { 
          ...row, 
          category: value, 
          sub_category: null, 
          item: null, 
          description: "", 
          rate: "", 
          amount: 0 
        };
        if (value) {
          try {
            const subcats = await fetchSubCategories(value.id);
            const list = subcats.results || subcats || [];
            setSubcategories((prev) => {
              const copy = [...prev];
              copy[index] = list;
              return copy;
            });
            
            const itemData = await fetchItemsByCategoryAndSubcategory(value.id, 0, 1, "");
            const rawList = itemData.results || itemData || [];
            
            // Item options me pure fields (with rates) hold kar rahe hain
            const mapped = rawList.map((it) => ({
              ...it,
              id: it.id,
              name: it.name,
              description: it.description || it.name,
              unit: it.unit,
              unit_name: it.unit_name || it.unit || "",
              rate: it.rate || it.unit_price || it.selling_price || it.price || 0,
            }));
            setItemsMap((prev) => ({ ...prev, [index]: mapped }));
          } catch (err) {
            console.error("fetch cat items error", err);
          }
        } else {
          setItemsMap((prev) => ({ ...prev, [index]: [] }));
        }
        break;

      case "sub_category":
        row = { ...row, sub_category: value, item: null, rate: "", amount: 0 };
        if (value && row.category) {
          try {
            const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, value.subcat_id || value.id, 1, "");
            const rawList = itemData.results || itemData || [];
            const mapped = rawList.map((it) => ({
              ...it,
              id: it.id,
              name: it.name,
              description: it.description || it.name,
              unit: it.unit,
              unit_name: it.unit_name || it.unit || "",
              rate: it.rate || it.unit_price || it.selling_price || it.price || 0,
            }));
            setItemsMap((prev) => ({ ...prev, [index]: mapped }));
          } catch {}
        }
        break;

      case "item":
        const autoRate = parseFloat(value?.rate || value?.unit_price || value?.selling_price || value?.price || 0) || 0;
        const autoUnit = value?.unit_name || value?.unit || row.unit || "";

        row = {
          ...row,
          item: value,
          description: value?.description || value?.name || row.description || "",
          unit: autoUnit,
          rate: autoRate > 0 ? autoRate.toString() : "", // Auto-fill rate here
        };
        break;

      case "unit":
        row = { ...row, unit: value?.id || value?.name || value || "" };
        break;
    }

    // Dynamic Calculation Update
    const qty = parseFloat(row.quantity) || 0;
    const rate = parseFloat(row.rate) || 0;
    row.amount = qty * rate;

    updated[index] = row;
    setLineItems(updated);
  };

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (!newValue || newValue.trim() === "") {
        if (type === "category") {
          const catData = await getCategories();
          setCategories(Array.isArray(catData) ? catData : catData.results || []);
        }
        if (type === "sub_category" && row.category) {
          const subcatData = await fetchSubCategories(row.category.id);
          setSubcategories((prev) => {
            const copy = [...prev];
            copy[index] = subcatData.results || subcatData;
            return copy;
          });
        }
        if (type === "item" && row.category) {
          const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, row.sub_category?.subcat_id || 0);
          const rawList = itemData.results || itemData || [];
          setItemsMap((prev) => ({
            ...prev,
            [index]: rawList.map((it) => ({
              ...it,
              id: it.id,
              name: it.name,
              description: it.description,
              unit: it.unit,
              unit_name: it.unit_name || it.unit || "",
              rate: it.rate || it.unit_price || it.selling_price || it.price || 0,
            })),
          }));
        }
        return;
      }

      if (type === "category") {
        const catData = await getCategories(1, newValue);
        setCategories(Array.isArray(catData.results) ? catData.results : catData || []);
      }
      if (type === "sub_category" && row.category) {
        const subcatData = await fetchSubCategories(row.category.id, 1, newValue);
        setSubcategories((prev) => {
          const copy = [...prev];
          copy[index] = subcatData.results || subcatData;
          return copy;
        });
      }
      if (type === "item" && row.category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(row.category.id, row.sub_category?.subcat_id || 0, 1, newValue);
        const rawList = itemData.results || [];
        const mapped = rawList.map((it) => ({
          ...it,
          id: it.id,
          name: it.name,
          description: it.description,
          unit: it.unit,
          unit_name: it.unit_name || it.unit || "",
          rate: it.rate || it.unit_price || it.selling_price || it.price || 0,
        }));
        setItemsMap((prev) => ({ ...prev, [index]: mapped }));
      }
    }, 500),
    []
  );

  const addLineItem = () => {
    if (lineItems.length >= 50) {
      toast.warning("Maximum 50 items allowed!");
      return;
    }
    setLineItems((prev) => [...prev, { ...initialLineItem }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) {
      toast.error("At least one item required!");
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index));
    setSubcategories((prev) => prev.filter((_, i) => i !== index));
    setItemsMap((prev) => {
      const newMap = {};
      Object.keys(prev).forEach((k) => {
        const ki = parseInt(k);
        if (ki < index) newMap[ki] = prev[ki];
        if (ki > index) newMap[ki - 1] = prev[ki];
      });
      return newMap;
    });
  };

  const handleNoteChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].note_text = value;
      return updated;
    });
  };

  const addNote = () => {
    if (notes.length >= 10) {
      toast.warning("Maximum 10 notes allowed!");
      return;
    }
    setNotes((prev) => [...prev, { ...initialNote }]);
  };

  const removeNote = (index) => {
    if (notes.length === 1) {
      setNotes([{ note_text: "" }]);
      return;
    }
    setNotes((prev) => prev.filter((_, i) => i !== index));
  };

  const validateFields = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    const newErrors = {
      email: emailRegex.test(formData.email) ? "" : "Invalid email",
      phone: phoneRegex.test(formData.phone) ? "" : "Enter valid 10-digit phone",
    };
    setErrors(newErrors);

    let itemsValid = true;
    lineItems.forEach((item, idx) => {
      if (!item.category && !item.item) {
        itemsValid = false;
        toast.error(`Item ${idx + 1}: Please select category/item`);
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        itemsValid = false;
        toast.error(`Item ${idx + 1}: Please enter quantity`);
      }
      if (!item.unit) {
        itemsValid = false;
        toast.error(`Item ${idx + 1}: Please select unit`);
      }
    });

    if (!formData.state) {
      toast.error("Please select a state");
      return false;
    }
    if (!formData.city) {
      toast.error("Please select a city");
      return false;
    }

    return !newErrors.email && !newErrors.phone && itemsValid;
  };

  const handleSaveDraft = () => {
    const draftData = {
      formData,
      lineItems,
      notes,
      termsAndConditions: [],
    };
    sessionStorage.setItem("enquiryDraft", JSON.stringify(draftData));
    toast.info("Enquiry draft saved locally!");
  };

  useEffect(() => {
    const savedDraft = sessionStorage.getItem("enquiryDraft");
    if (savedDraft && !isEditMode) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.lineItems?.length > 0) setLineItems(parsed.lineItems);
        if (parsed.notes?.length > 0) setNotes(parsed.notes);
        toast.info("Loaded Enquiry draft");
      } catch (e) {
        console.error("Draft parse error", e);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("User not logged in.");
      return;
    }

    const filteredNotes = notes.filter((note) => note.note_text.trim() !== "");

    const cleanedItems = lineItems.map((it) => ({
      category: it.category?.id || it.category || null,
      category_name: it.category?.name || "",
      sub_category: it.sub_category?.subcat_id || it.sub_category?.id || it.sub_category || null,
      sub_category_name: it.sub_category?.name || "",
      item: it.item?.id || it.item || null,
      item_name: it.item?.name || it.description || "",
      description: it.description || it.item?.name || "",
      quantity: parseFloat(it.quantity) || 0,
      unit: it.unit?.id || it.unit || "",
      unit_name: it.unit?.name || it.unit || "",
      rate: parseFloat(it.rate) || 0,
      amount: parseFloat(it.amount) || 0,
    }));

    const total = cleanedItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

    const dataToSend = {
      ...formData,
      enquiry_no: formData.enquiry_no,
      follow_up_date: formData.follow_up_date ? formData.follow_up_date : null,
      line_items: cleanedItems,
      items: cleanedItems,
      notes: filteredNotes,
      total_amount: total,
      grand_total: total,
      created_by: parseInt(userId),
      division: storedDivision?.division_id || formData.division || "",
    };

    setLoading(true);
    try {
      if (isEditMode) {
        await updateEnquiry(enquiryData.enquiry_id, dataToSend);
        toast.success("Enquiry updated successfully!");
      } else {
        const res = await createEnquiry(dataToSend);
        toast.success(`Enquiry created! ${res.enquiry_no || ""}`);
      }
      sessionStorage.removeItem("enquiryDraft");
      setTimeout(() => navigate("/dashboard/Enquiry"), 1200);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || error.response?.data?.message || "Failed to submit!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ mt: 0, pt: 0, px: 0 }}>
      <ToastContainer />
      <form onSubmit={handleSubmit} autoComplete="off">
        {/* BUTTONS */}
        {isMobile ? (
          <Grid container spacing={1} sx={{ mb: 2, mt: { xs: 4, sm: 0, md: -3 } }}>
            <Grid item xs={4}>
              <Button
                fullWidth
                type="button"
                variant="outlined"
                startIcon={<DraftsIcon />}
                onClick={handleSaveDraft}
                sx={{
                  height: 40,
                  fontWeight: 600,
                  color: "#ff8400",
                  borderColor: "#ff8400",
                  fontSize: "0.75rem",
                  "&:hover": { borderColor: "#e67300", bgcolor: "#fff3e0" },
                }}
              >
                Draft
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth type="submit" variant="contained" disabled={loading} sx={{ height: 40 }}>
                {loading ? <CircularProgress size={18} color="inherit" /> : "Submit"}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth variant="outlined" onClick={() => navigate("/dashboard/Enquiry")} sx={{ height: 40 }}>
                Back
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, mt: -3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {isEditMode ? "Edit Enquiry" : "Create Enquiry"}{" "}
              {formData.enquiry_no && <span style={{ color: "#1976d2", fontSize: "0.8em" }}>({formData.enquiry_no})</span>}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<DraftsIcon />}
                onClick={handleSaveDraft}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  color: "#ff8400",
                  borderColor: "#ff8400",
                  "&:hover": { borderColor: "#e67300", bgcolor: "#fff3e0" },
                }}
              >
                Save Draft
              </Button>
              <Button type="submit" variant="contained" disabled={loading} sx={{ textTransform: "none" }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : "Submit Enquiry"}
              </Button>
              <Button variant="outlined" onClick={() => navigate("/dashboard/Enquiry")} sx={{ textTransform: "none" }}>
                ← Back
              </Button>
            </Box>
          </Box>
        )}

        {/* Customer Info */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#1976d2" }}>
              <EditNoteIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Enquiry No." value={formData.enquiry_no} disabled size="small" helperText="Auto unique" />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={partyOptions}
                  getOptionLabel={(option) => option.company_name || option.name || ""}
                  value={partyOptions.find((o) => (o.company_name || o.name) === formData.company_name) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const phoneValue = newValue.contact || newValue.phone || newValue.mobile || "";
                      
                      let unifiedContacts = [];
                      if (newValue.name) {
                        unifiedContacts.push({
                          title: "",
                          contact_name: newValue.name,
                          contact_no: newValue.contact || "",
                          designation: "Primary Contact",
                        });
                      }
                      
                      const additionalContacts = (newValue.contact_persons || []).filter(
                        (cp) => cp.contact_name?.toLowerCase() !== newValue.name?.toLowerCase()
                      );

                      const mergedContacts = [...unifiedContacts, ...additionalContacts];
                      setContactPersons(mergedContacts);
                      setSelectedContactPerson(null);

                      setFormData((prev) => ({
                        ...prev,
                        company_name: newValue.company_name || "",
                        customer_name: newValue.name || "",
                        email: newValue.email || "",
                        phone: phoneValue.replace(/\D/g, ""),
                        address: newValue.address || "",
                        city: newValue.city || prev.city,
                        state: newValue.state || prev.state,
                      }));
                    } else {
                      setContactPersons([]);
                      setSelectedContactPerson(null);
                      setFormData((prev) => ({
                        ...prev,
                        company_name: "",
                        customer_name: "",
                        email: "",
                        phone: "",
                        address: "",
                      }));
                    }
                  }}
                  onInputChange={(e, v, r) => {
                    if (r === "input") setPartySearch(v);
                  }}
                  isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                  loading={partyLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company Name (Debtor)"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {partyLoading && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={contactPersons}
                  getOptionLabel={(option) => {
                    const title = option.title || "";
                    const name = option.contact_name || "";
                    const designation = option.designation || "";
                    return `${title} ${name} ${designation ? `(${designation})` : ""}`.trim();
                  }}
                  value={selectedContactPerson}
                  onChange={(e, newValue) => {
                    setSelectedContactPerson(newValue);
                    if (newValue) {
                      setFormData((prev) => ({
                        ...prev,
                        customer_name: `${newValue.title || ""} ${newValue.contact_name || ""}`.trim(),
                        phone: (newValue.contact_no || "").replace(/\D/g, ""),
                        email: newValue.email || prev.email,
                      }));
                    }
                  }}
                  isOptionEqualToValue={(option, val) => option.contact_name === val?.contact_name}
                  noOptionsText="No contact persons found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact Person"
                      size="small"
                      placeholder={contactPersons.length === 0 ? "Select company first" : "Select contact person"}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                          {option.title} {option.contact_name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
                          📞 {option.contact_no || "N/A"} | 💼 {option.designation || "N/A"}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required size="small" />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} inputProps={{ maxLength: 10 }} error={!!errors.phone} helperText={errors.phone} required size="small" />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} size="small" />
              </Grid>

              <Grid item xs={12} sm={2}>
                <Autocomplete
                  options={states}
                  getOptionLabel={(o) => o.name || ""}
                  value={states.find((s) => s.name === formData.state) || null}
                  onChange={(e, v) => {
                    setFormData((prev) => ({ ...prev, state: v?.name || "", city: "" }));
                    if (v) fetchCitiesForState(v.iso2);
                  }}
                  isOptionEqualToValue={(option, val) => option.id === val?.id}
                  loading={loadingStates}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStates && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(o) => o.name || ""}
                  value={cities.find((c) => c.name === formData.city) || null}
                  onChange={(e, v) => setFormData((prev) => ({ ...prev, city: v?.name || "" }))}
                  isOptionEqualToValue={(option, val) => option.id === val?.id}
                  loading={loadingCities}
                  disabled={!formData.state}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCities && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField fullWidth label="Country" name="country" value={formData.country} onChange={handleChange} size="small" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1976d2" }}>
                📦 Enquiry Items
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addLineItem} size="small">
                Add Item
              </Button>
            </Box>

            {isMobile ? (
              <Box>
                {lineItems.map((item, index) => (
                  <Paper key={index} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Item #{index + 1}
                      </Typography>
                      {lineItems.length > 1 && (
                        <IconButton color="error" onClick={() => removeLineItem(index)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Autocomplete
                        options={categories}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={item.category || null}
                        onChange={(e, val) => handleItemAutocompleteChange(index, "category", val)}
                        onInputChange={(e, newVal, reason) => {
                          if (reason === "input") handleItemInputChange(index, item, newVal, "category");
                        }}
                        isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                        renderInput={(params) => <TextField {...params} label="Category" size="small" required />}
                      />
                      <Autocomplete
                        options={itemsMap[index] || []}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={item.item || null}
                        onChange={(e, val) => handleItemAutocompleteChange(index, "item", val)}
                        onInputChange={(e, newVal) => handleItemInputChange(index, item, newVal, "item")}
                        isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                        renderInput={(params) => <TextField {...params} label="Item" size="small" required />}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Autocomplete
                          options={units}
                          getOptionLabel={(o) => o.name || o.unit_name || o}
                          value={units.find((u) => u.id === item.unit || u.name === item.unit) || (item.unit ? { name: item.unit } : null)}
                          onChange={(e, val) => handleItemAutocompleteChange(index, "unit", val)}
                          isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                          renderInput={(params) => <TextField {...params} label="Unit" size="small" required />}
                          fullWidth
                        />
                        <TextField
                          label="Qty"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                          size="small"
                          fullWidth
                          required
                        />
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField label="Rate" type="number" value={item.rate} onChange={(e) => handleLineItemChange(index, "rate", e.target.value)} size="small" fullWidth />
                        <TextField label="Amount" value={item.amount} disabled size="small" fullWidth />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 0, border: "1px solid #e0e0e0", overflowX: "auto" }}>
                <Box sx={{ minWidth: 1000 }}>
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                        <TableCell sx={{ width: "15%" }}>Category</TableCell>
                        <TableCell sx={{ width: "15%" }}>Item</TableCell>
                        <TableCell sx={{ width: "8%" }}>Unit</TableCell>
                        <TableCell sx={{ width: "10%" }}>Qty</TableCell>
                        <TableCell sx={{ width: "10%" }}>Rate</TableCell>
                        <TableCell sx={{ width: "12%" }}>Amount</TableCell>
                        <TableCell sx={{ width: "5%" }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((row, index) => (
                        <TableRow key={index}>
                          {/* Category */}
                          <TableCell>
                            <Autocomplete
                              options={categories}
                              getOptionLabel={(opt) => opt.name || ""}
                              value={row.category || null}
                              onChange={(e, val, reason) => {
                                if (reason === "clear" || !val) {
                                  handleItemAutocompleteChange(index, "category", null);
                                  return;
                                }
                                handleItemAutocompleteChange(index, "category", val);
                              }}
                              onInputChange={(e, newVal, reason) => {
                                if (reason === "input") handleItemInputChange(index, row, newVal, "category");
                              }}
                              isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                              renderInput={(params) => <TextField {...params} label="Category" size="small" />}
                            />
                          </TableCell>

                          {/* Item */}
                          <TableCell>
                            <Autocomplete
                              options={itemsMap[index] || []}
                              getOptionLabel={(opt) => opt.name || ""}
                              value={row.item || null}
                              onChange={(e, val) => handleItemAutocompleteChange(index, "item", val)}
                              onInputChange={(e, newVal) => handleItemInputChange(index, row, newVal, "item")}
                              isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                              renderInput={(params) => <TextField {...params} label="Item" size="small" />}
                            />
                          </TableCell>

                          {/* Unit */}
                          <TableCell>
                            <Autocomplete
                              options={units}
                              getOptionLabel={(o) => o.name || o.unit_name || ""}
                              value={units.find((u) => u.id === row.unit || u.name === row.unit) || null}
                              onChange={(e, val) => {
                                const copy = [...lineItems];
                                copy[index].unit = val?.id || val?.name || "";
                                setLineItems(copy);
                              }}
                              isOptionEqualToValue={(option, val) => (option?.id || option) === (val?.id || val)}
                              renderInput={(params) => <TextField {...params} label="Unit" size="small" />}
                            />
                          </TableCell>

                          {/* Qty */}
                          <TableCell>
                            <TextField
                              type="number"
                              value={row.quantity}
                              onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </TableCell>

                          {/* Rate */}
                          <TableCell>
                            <TextField
                              type="number"
                              value={row.rate}
                              onChange={(e) => handleLineItemChange(index, "rate", e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </TableCell>

                          {/* Amount */}
                          <TableCell>
                            <TextField value={row.amount} disabled size="small" fullWidth />
                          </TableCell>

                          {/* Action */}
                          <TableCell>
                            <IconButton color="error" onClick={() => removeLineItem(index)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={5} align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell colSpan={2}>
                          <strong>₹ {lineItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(2)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1976d2" }}>
                📝 Notes
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addNote} size="small">
                Add Note
              </Button>
            </Box>
            {notes.map((note, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField fullWidth size="small" placeholder="Note" value={note.note_text} onChange={(e) => handleNoteChange(idx, e.target.value)} />
                <IconButton color="error" onClick={() => removeNote(idx)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Enquiry Details */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#1976d2" }}>
              ⚙️ Enquiry Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enquiry Source"
                  value={formData.enquiry_source}
                  onChange={(e) => setFormData((p) => ({ ...p, enquiry_source: e.target.value }))}
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  <option value=""></option>
                  {["Website", "Phone Call", "Email", "Sales Visit", "Reference", "Other"].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="date" label="Follow Up Date" value={formData.follow_up_date} onChange={(e) => setFormData((p) => ({ ...p, follow_up_date: e.target.value }))} InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  {["New", "Under Review", "Quoted", "Converted", "Closed - Lost"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </Container>
  );
}